import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnimatedKpiCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: LucideIcon;
  format?: 'number' | 'currency' | 'percentage';
  colorScheme?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  isLoading?: boolean;
  customColor?: string;
}

export const AnimatedKpiCard: React.FC<AnimatedKpiCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  format = 'number',
  colorScheme = 'blue',
  isLoading = false,
  customColor
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isLoading) return;
    
    let startTime: number;
    const duration = 1000; // 1 seconde d'animation
    const startValue = 0;
    const endValue = value;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function pour une animation fluide
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (endValue - startValue) * easeOutQuart;

      setDisplayValue(Math.floor(currentValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, isLoading]);

  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR'
        }).format(val);
      case 'percentage':
        return `${val}%`;
      default:
        return new Intl.NumberFormat('fr-FR').format(val);
    }
  };

  const colorClasses = {
    blue: {
      iconBg: 'bg-gradient-to-br from-blue-500/10 to-blue-600/20',
      icon: 'text-blue-600',
      title: 'text-blue-700 dark:text-blue-300',
      value: 'text-blue-800 dark:text-blue-200',
      subtitle: 'text-blue-600 dark:text-blue-400'
    },
    green: {
      iconBg: 'bg-gradient-to-br from-green-500/10 to-green-600/20',
      icon: 'text-green-600',
      title: 'text-green-700 dark:text-green-300',
      value: 'text-green-800 dark:text-green-200',
      subtitle: 'text-green-600 dark:text-green-400'
    },
    purple: {
      iconBg: 'bg-gradient-to-br from-purple-500/10 to-purple-600/20',
      icon: 'text-purple-600',
      title: 'text-purple-700 dark:text-purple-300',
      value: 'text-purple-800 dark:text-purple-200',
      subtitle: 'text-purple-600 dark:text-purple-400'
    },
    orange: {
      iconBg: 'bg-gradient-to-br from-orange-500/10 to-orange-600/20',
      icon: 'text-orange-600',
      title: 'text-orange-700 dark:text-orange-300',
      value: 'text-orange-800 dark:text-orange-200',
      subtitle: 'text-orange-600 dark:text-orange-400'
    },
    red: {
      iconBg: 'bg-gradient-to-br from-red-500/10 to-red-600/20',
      icon: 'text-red-600',
      title: 'text-red-700 dark:text-red-300',
      value: 'text-red-800 dark:text-red-200',
      subtitle: 'text-red-600 dark:text-red-400'
    }
  };

  const colors = colorClasses[colorScheme];

  if (isLoading) {
    return (
      <Card 
        className="animate-pulse group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50"
        role="status"
        aria-label="Chargement des données"
      >
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-muted" aria-hidden="true"></div>
            <div className="flex-1">
              <div className="h-4 bg-muted rounded mb-2" aria-hidden="true"></div>
              <div className="h-8 bg-muted rounded" aria-hidden="true"></div>
            </div>
          </div>
        </CardHeader>
        <span className="sr-only">Chargement en cours...</span>
      </Card>
    );
  }

  return (
    <Card 
      className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50 animate-fade-in cursor-pointer"
      role="region"
      aria-label={`${title}: ${formatValue(displayValue)}`}
    >
      <CardHeader>
        <div className="flex items-center gap-3">
          <div 
            className={cn('p-3 rounded-lg transition-transform duration-200 hover:scale-105', !customColor && colors.iconBg)}
            style={customColor ? { 
              backgroundColor: customColor + '20',
              background: `linear-gradient(135deg, ${customColor}10, ${customColor}20)`
            } : undefined}
            aria-hidden="true"
          >
            <Icon 
              className={cn('w-6 h-6', !customColor && colors.icon)}
              style={customColor ? { color: customColor } : undefined}
            />
          </div>
          <div className="flex-1">
            <p 
              className={cn('text-sm font-medium mb-1', !customColor && colors.title)}
              style={customColor ? { color: customColor } : undefined}
            >
              {title}
            </p>
            <p 
              className={cn('text-3xl font-bold', !customColor && colors.value)}
              style={customColor ? { color: customColor } : undefined}
              aria-live="polite"
            >
              {formatValue(displayValue)}
            </p>
            {subtitle && (
              <p 
                className={cn('text-xs mt-1', !customColor && colors.subtitle)}
                style={customColor ? { color: customColor + '80' } : undefined}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};